import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { LifeBuoy, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { actionsApi } from '@/api/actions';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type {
  FeedbackBlock,
  FeedbackEscalateResponse,
  Language,
  SupportInput,
  SupportResponse,
} from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DecisionCard } from './DecisionCard';
import { DEFAULT_LANGUAGE, LanguageSelect } from './LanguageSelect';
import { PromptVersionSelect } from './PromptVersionSelect';

export function SupportPanel({
  initialPromptVersion,
}: {
  initialPromptVersion?: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId, currentBot } = useWorkspace();
  const [message, setMessage] = React.useState('');
  const [shipmentId, setShipmentId] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [language, setLanguage] = React.useState<Language>(DEFAULT_LANGUAGE);
  const [promptVersion, setPromptVersion] = React.useState<number | null>(initialPromptVersion ?? null);
  const [result, setResult] = React.useState<SupportResponse | null>(null);

  const mutation = useMutation({
    mutationFn: (input: SupportInput) => actionsApi.support(input, language),
    onSuccess: (data) => {
      setResult(data);
      if (data.decision.kind === 'failed')
        toast.error(t('chat.playground.failedToast', { reason: data.decision.reason }));
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('common.selectBotInTopbar')}
      </div>
    );
  }

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast.error(t('chat.support.tooShort'));
      return;
    }
    mutation.mutate({
      message: message.trim(),
      ...(shipmentId.trim() ? { shipmentId: shipmentId.trim() } : {}),
      ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
      ...(category.trim() ? { category: category.trim() } : {}),
      ...(promptVersion !== null ? { promptVersion } : {}),
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LifeBuoy className="h-4 w-4" />
            {t('chat.support.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            <Trans
              i18nKey="chat.support.subtitle"
              values={{ bot: currentBot?.name ?? currentBotId }}
              components={{ mono: <span className="font-mono" />, code: <code /> }}
            />
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="sp-msg">{t('chat.support.clientMessage')}</Label>
              <Textarea
                id="sp-msg"
                rows={5}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('chat.support.clientMessagePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="sp-shipment">{t('chat.support.shipmentId')}</Label>
              <Input
                id="sp-shipment"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="SHP-12345"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sp-email">{t('chat.support.contactEmail')}</Label>
                <Input
                  id="sp-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <Label htmlFor="sp-cat">{t('chat.support.category')}</Label>
                <Input
                  id="sp-cat"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={t('chat.support.categoryPlaceholder')}
                />
              </div>
            </div>
            <LanguageSelect value={language} onChange={setLanguage} />
            <PromptVersionSelect value={promptVersion} onChange={setPromptVersion} />
            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending} className="gap-2">
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {t('chat.diagnose.run')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {mutation.isPending ? (
          <Card>
            <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('chat.diagnose.orchestratorRunning')}
            </CardContent>
          </Card>
        ) : result ? (
          <>
            <DecisionCard
              decision={result.decision}
              usage={result.usage}
              latencyMs={result.latencyMs}
              sessionId={result.sessionId}
            />
            {result.feedback ? <FeedbackHitl feedback={result.feedback} /> : null}
          </>
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t('chat.support.resultPlaceholder')}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function FeedbackHitl({ feedback }: { feedback: FeedbackBlock }): JSX.Element {
  const { t } = useTranslation();
  const [comment, setComment] = React.useState('');
  const [outcome, setOutcome] = React.useState<
    { kind: 'satisfied' } | { kind: 'escalated'; data: FeedbackEscalateResponse } | null
  >(null);

  const satisfiedMutation = useMutation({
    mutationFn: () => actionsApi.feedbackSatisfied(feedback.feedbackId),
    onSuccess: () => {
      setOutcome({ kind: 'satisfied' });
      toast.success(t('chat.support.feedbackToast'));
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const escalateMutation = useMutation({
    mutationFn: () =>
      actionsApi.feedbackEscalate(feedback.feedbackId, comment.trim() ? { comment: comment.trim() } : {}),
    onSuccess: (data) => {
      setOutcome({ kind: 'escalated', data });
      const succeeded = data.channels.filter((c) => c.ok).map((c) => c.channel);
      toast.success(
        succeeded.length > 0
          ? t('chat.support.escalatedToast', { channels: succeeded.join(', ') })
          : t('chat.support.escalatedPartialToast'),
      );
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (outcome?.kind === 'satisfied') {
    return (
      <Card>
        <CardContent className="p-4 text-sm">
          <Badge variant="success">{t('chat.support.satisfied')}</Badge>{' '}
          <span className="ml-2 text-muted-foreground">
            {t('chat.support.feedbackBadge', { id: feedback.feedbackId.slice(0, 8) })}
          </span>
        </CardContent>
      </Card>
    );
  }
  if (outcome?.kind === 'escalated') {
    return (
      <Card>
        <CardContent className="space-y-2 p-4 text-sm">
          <div>
            <Badge variant="secondary">{t('chat.support.escalated')}</Badge>{' '}
            <span className="ml-2">{outcome.data.subject}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('chat.support.priority', { priority: outcome.data.priority })}
          </p>
          <ul className="space-y-1">
            {outcome.data.channels.map((c) => (
              <li key={c.channel} className="flex items-center gap-2 text-xs">
                <Badge variant={c.ok ? 'success' : 'destructive'} className="text-[10px] capitalize">
                  {c.channel}
                </Badge>
                <span className="text-muted-foreground">
                  {c.ok
                    ? t('chat.support.channelOk', { ref: c.ref })
                    : c.error ?? t('chat.support.channelFailed')}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    );
  }

  const busy = satisfiedMutation.isPending || escalateMutation.isPending;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{feedback.question}</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          <Trans
            i18nKey="chat.support.feedbackId"
            values={{
              id: feedback.feedbackId.slice(0, 8),
              date: new Date(feedback.expiresAt).toLocaleString(),
            }}
            components={{ mono: <code className="font-mono" /> }}
          />
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          variant="default"
          className="w-full gap-2"
          disabled={busy}
          onClick={() => satisfiedMutation.mutate()}
        >
          {feedback.actions.find((a) => a.type === 'satisfied')?.label ?? t('chat.support.defaultSatisfied')}
        </Button>
        <details className="rounded-md border bg-muted/20 p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            {feedback.actions.find((a) => a.type === 'escalate')?.label ?? t('chat.support.defaultEscalate')}
          </summary>
          <div className="mt-3 space-y-2">
            <Label htmlFor="esc-cm" className="text-xs">
              {t('chat.support.whyEscalate')}
            </Label>
            <Textarea
              id="esc-cm"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('chat.support.whyEscalatePlaceholder')}
            />
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => escalateMutation.mutate()}
              className="gap-2"
            >
              {escalateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('chat.support.escalateAction')}
            </Button>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}
